import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Rocket } from "lucide-react";
import { MOCK_EVENTS } from "@/lib/mockData";
import { useAuth } from "@/context/AuthContext";
import { isBoostActive } from "@/lib/monetizationStore";
import { useMoneVersion } from "@/lib/hooks";
import MoreMenu from "@/components/MoreMenu";

const fmtPrice = (n, c) => (n === 0 ? "Free" : `${c}${n.toLocaleString()}`);

export const EventCard = ({ event, onClick, testId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useMoneVersion();
    const boosted = user ? isBoostActive(user.id, "event", event.id) : false;
    void v;
    const handle = onClick || (() => navigate(`/events/${event.id}`));
    const isOwner = !!(user && event?.ownerId && user.id === event.ownerId);
    return (
        <div
            data-testid={testId || `event-card-${event.id}`}
            onClick={handle}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handle(); }}
            role="button"
            tabIndex={0}
            className="relative w-full text-left rounded-[22px] overflow-hidden shadow-card active:scale-[0.98] transition-transform duration-200 cursor-pointer"
        >
            <div className="relative aspect-[16/10] bg-muted">
                {event.image ? (
                    <img
                        src={event.image}
                        alt={event.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : null}
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.82) 100%)",
                    }}
                />

                {boosted && (
                    <span
                        data-testid={`event-boosted-${event.id}`}
                        className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-foreground shadow-card"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        }}
                    >
                        <Rocket size={10} strokeWidth={2.6} />
                        Boosted
                    </span>
                )}

                <span
                    className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11.5px] font-semibold backdrop-blur-md bg-background/85 text-foreground border border-border shadow-soft"
                    data-testid={`event-price-${event.id}`}
                >
                    {fmtPrice(event.price, event.currency)}
                </span>

                {!boosted && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-primary/90 text-primary-foreground">
                        {event.category}
                    </span>
                )}

                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <h3 className="font-display text-[17px] font-semibold leading-tight line-clamp-1">
                        {event.title}
                    </h3>
                    <div className="mt-1 flex items-center gap-3 text-[12px] text-white/85">
                        <span className="inline-flex items-center gap-1">
                            <Calendar size={12} strokeWidth={2.4} />
                            {event.date.split(" · ")[0]}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={12} strokeWidth={2.4} />
                            <span className="line-clamp-1">{event.location}</span>
                        </span>
                    </div>
                </div>
            </div>
            {!isOwner && (
                <div
                    className="absolute top-2 right-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <MoreMenu
                        target={{ kind: "event", id: event.id, name: event.title }}
                        testId={`event-card-more-${event.id}`}
                        position="right"
                        className="h-8 w-8 rounded-full bg-background/90 border border-border/80 shadow-soft hover:bg-background"
                    />
                </div>
            )}
        </div>
    );
};

/** Horizontal scroller of featured events (large image cards). */
export const FeaturedEventsRow = ({ events = MOCK_EVENTS }) => {
    return (
        <div
            className="flex gap-3.5 px-5 overflow-x-auto no-scrollbar snap-x snap-mandatory"
            data-testid="featured-events-row"
        >
            {events.map((ev) => (
                <div key={ev.id} className="shrink-0 w-[82%] max-w-[360px] snap-start">
                    <EventCard event={ev} />
                </div>
            ))}
        </div>
    );
};
